import { isWallIncidentArtifact } from '@tsr/shared-types';
import { isEscalationStale, type WallTarget } from './selectionBridge';

/**
 * WALL incident-artifact derivation (#428, PART XI Ruling 1).
 *
 * The wall base is the four ambient WALL_VIEW_LAYERS; the escalated incident's
 * OWN overlays (evac ORDER/ALERT polygons + the gated 3D flood model) are a
 * SEPARATE, context-triggered set (`WALL_INCIDENT_ARTIFACT_LAYERS`) that WallMap
 * un-suppresses ONLY while a `kind:'incident'` target is on the wall — never from
 * an operator's pref layers (Ruling 1.7: per-operator prefs never touch the wall).
 *
 * Two pure steps, both unit-testable (the wall supplies the clock so there is no
 * `Date.now()` here):
 *   1. `wallIncidentContext` — resolve the active escalation to the incident scope
 *      (or null: a camera / stale / cleared pin has no artifacts).
 *   2. `deriveWallIncidentLayerProps` — project that scope onto the five WallMap
 *      layer props that were hard-coded off. With NO active incident every artifact
 *      is off (no raw `true`) — the "off-without-incident" invariant WallMap.test
 *      pins.
 */

/**
 * The context-triggered incident scope resolved from the active wall escalation.
 * Present ONLY while a NON-STALE `kind:'incident'` pin is on the wall.
 */
export interface WallIncidentContext {
  /** Resolved tracked incidents.id for the sector overlay — null when the escalated
   *  incident has no resolved id (non-sectored source / not yet promoted). Distinct
   *  from the raw source-record id the wall keys the pin by. */
  incidentId: string | null;
  /** Incident centroid for the owned-incident scope (drone-coverage scope + fly). */
  lat: number | null;
  lon: number | null;
}

/**
 * Resolve the escalation into the incident-artifact scope (pure). A camera pin, a
 * stale pin (past its TTL — the calm-room auto-clear, which is how a no-longer-current
 * incident leaves the wall), or no pin resolves to null → the base wall.
 */
export function wallIncidentContext(
  escalation: WallTarget | null,
  nowMs: number,
): WallIncidentContext | null {
  if (!escalation || escalation.kind !== 'incident') return null;
  if (isEscalationStale(escalation, nowMs)) return null;
  return {
    incidentId: escalation.incidentId ?? null,
    lat: escalation.lat ?? null,
    lon: escalation.lon ?? null,
  };
}

/**
 * FLOOD-MODEL WALL GATE (#428 next-slice, founder-gated).
 *
 * `flood-inundation` is in the wall incident-artifact lock set (context-triggered),
 * but its heavy Cesium mount (World Terrain swap + OSM Buildings on the unattended
 * wall) AND its mandatory persistent on-wall "MODELLED — not an official inundation
 * product" caveat (the wall is read-only — no click-to-caveat; a caveat-less blue
 * inundation surface on government glass reads as official = an honesty + doctrine
 * defect) are BOTH a founder Cesium eyeball [FLAG-HUMAN] away. Until that gate
 * clears, the model stays OFF even for an active incident — evac + framing ship
 * first, the flood model fast-follows. (Mirrors the wall radar `wallDefaultOn` gate
 * that shipped false until the founder screenshot cleared.) Flip to true in the same
 * commit that mounts the on-wall caveat + records the founder eyeball.
 */
export const WALL_FLOOD_MODEL_ENABLED = false;

/** The BC/EMBC evac-order layer renders through the generalized public-ArcGIS
 *  renderer under the `evacuation` category (arcgisCategoryVisibility key). */
const ARCGIS_EVACUATION_CATEGORY = 'evacuation';

/** The five WallMap layer props the incident-artifact context drives (previously
 *  hard-coded off/null on the wall). */
export interface WallIncidentLayerProps {
  /** operator-declared evac orders (mig 0115) — a live layer. */
  showOperatorEvacOrders: boolean;
  /** 3D flood model — gated (WALL_FLOOD_MODEL_ENABLED) even for an active incident. */
  showFloodInundation: boolean;
  /** bc-evacuation-orders rides the arcgis `evacuation` category. */
  arcgisCategoryVisibility: Record<string, boolean>;
  /** Owned-incident centroid (drone-coverage scope; the incident's own hazard
   *  marker is already rendered by the wall hazard layers). */
  selectedIncidentPoint: { lat: number; lon: number } | null;
  /** The incident whose sectors render (null = none / unresolved → honest empty). */
  sectorIncidentId: string | null;
}

/** Stable empty maps/props for the base (no-incident) wall — reused so a steady
 *  base wall never hands MapView a fresh identity that would re-run its
 *  apply-visibility effects every refresh. */
const EMPTY_ARCGIS: Record<string, boolean> = Object.freeze({});
const BASE_WALL_INCIDENT_PROPS: Readonly<WallIncidentLayerProps> = Object.freeze({
  showOperatorEvacOrders: false,
  showFloodInundation: false,
  arcgisCategoryVisibility: EMPTY_ARCGIS,
  selectedIncidentPoint: null,
  sectorIncidentId: null,
});

/**
 * Derive the five WallMap layer props from the incident context. With NO active
 * incident (`ctx == null`) EVERY artifact is off — the base wall, no raw `true`.
 * Each artifact toggle derives from `isWallIncidentArtifact` (the lock), so a key
 * dropped from the lock structurally turns its wall artifact off.
 */
export function deriveWallIncidentLayerProps(
  ctx: WallIncidentContext | null,
): WallIncidentLayerProps {
  if (ctx == null) return BASE_WALL_INCIDENT_PROPS;
  return {
    // operator-evacuation-orders + bc-evacuation-orders: un-suppressed while an
    // incident is on the wall (each derived from the lock, not a raw literal).
    showOperatorEvacOrders: isWallIncidentArtifact('operator-evacuation-orders'),
    arcgisCategoryVisibility: isWallIncidentArtifact('bc-evacuation-orders')
      ? { [ARCGIS_EVACUATION_CATEGORY]: true }
      : EMPTY_ARCGIS,
    // flood-inundation is in the lock set, but its mount stays gated (next slice).
    showFloodInundation: WALL_FLOOD_MODEL_ENABLED && isWallIncidentArtifact('flood-inundation'),
    // Owned-incident framing: centroid (for drone-coverage scope) + the resolved
    // incident id for its sector overlay.
    selectedIncidentPoint: ctx.lat != null && ctx.lon != null ? { lat: ctx.lat, lon: ctx.lon } : null,
    sectorIncidentId: ctx.incidentId,
  };
}
